import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, 
    ArrowRight, 
    Brain, 
    Heart, 
    Bone, 
    Eye,
    Ear,
    Hand,
    Footprints,
    Wind,
    Droplets,
    Activity,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Camera,
    Upload,
    X,
    FileImage,
    Pill,
    Stethoscope,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    Video,
    CreditCard,
    Sparkles,
    Crown,
    Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bodyParts = [
    { id: 'head', label: 'Head', icon: Brain },
    { id: 'chest', label: 'Chest', icon: Heart },
    { id: 'abdomen', label: 'Abdomen', icon: Activity },
    { id: 'back', label: 'Back/Spine', icon: Bone },
    { id: 'arms', label: 'Arms/Hands', icon: Hand },
    { id: 'legs', label: 'Legs/Feet', icon: Footprints },
    { id: 'eyes', label: 'Eyes', icon: Eye },
    { id: 'ears', label: 'Ears/Throat', icon: Ear },
    { id: 'respiratory', label: 'Respiratory', icon: Wind },
    { id: 'skin', label: 'Skin', icon: Droplets },
];

// Detailed symptoms organized by body part with sub-categories
const detailedSymptomsByBodyPart = {
    head: {
        'Type of Pain': ['Headache', 'Migraine', 'Throbbing pain', 'Sharp pain', 'Dull ache', 'Pressure sensation', 'Tension headache'],
        'Location': ['Frontal (forehead)', 'Temporal (sides)', 'Occipital (back of head)', 'One-sided', 'All over'],
        'Neurological': ['Dizziness', 'Vertigo', 'Confusion', 'Memory problems', 'Difficulty concentrating', 'Fainting', 'Seizures', 'Numbness'],
        'Other': ['Scalp tenderness', 'Hair loss', 'Swelling', 'Lumps', 'Trauma/injury']
    },
    chest: {
        'Chest Pain': ['Sharp pain', 'Dull ache', 'Burning sensation', 'Tightness', 'Pressure', 'Crushing pain', 'Pain radiating to arm', 'Pain radiating to jaw'],
        'Heart Related': ['Palpitations', 'Rapid heartbeat', 'Slow heartbeat', 'Irregular heartbeat', 'Skipped beats', 'Fluttering'],
        'Breathing': ['Shortness of breath', 'Difficulty breathing when lying down', 'Wheezing', 'Painful breathing'],
        'Other': ['Swelling', 'Tenderness', 'Coughing', 'Coughing blood']
    },
    abdomen: {
        'Pain Location': ['Upper right', 'Upper left', 'Lower right', 'Lower left', 'Central/around navel', 'All over', 'Moving pain'],
        'Pain Type': ['Sharp', 'Cramping', 'Burning', 'Dull ache', 'Stabbing', 'Colicky'],
        'Digestive': ['Nausea', 'Vomiting', 'Bloating', 'Gas', 'Heartburn', 'Acid reflux', 'Difficulty swallowing'],
        'Bowel': ['Constipation', 'Diarrhea', 'Blood in stool', 'Black stools', 'Mucus in stool', 'Urgency', 'Incomplete evacuation'],
        'Other': ['Loss of appetite', 'Weight loss', 'Jaundice (yellowing)', 'Swelling', 'Tenderness']
    },
    back: {
        'Pain Location': ['Upper back', 'Middle back', 'Lower back', 'Neck', 'Tailbone', 'One side', 'Both sides'],
        'Pain Type': ['Sharp', 'Dull ache', 'Burning', 'Shooting', 'Stabbing', 'Throbbing'],
        'Characteristics': ['Constant pain', 'Intermittent pain', 'Pain with movement', 'Pain at rest', 'Morning stiffness', 'Night pain'],
        'Associated Symptoms': ['Radiating to legs', 'Numbness', 'Tingling', 'Weakness', 'Difficulty walking', 'Loss of bladder control'],
        'Other': ['Muscle spasms', 'Limited range of motion', 'Visible deformity', 'Trauma/injury']
    },
    arms: {
        'Location': ['Shoulder', 'Upper arm', 'Elbow', 'Forearm', 'Wrist', 'Hand', 'Fingers'],
        'Pain Type': ['Sharp', 'Dull ache', 'Burning', 'Shooting', 'Throbbing', 'Cramping'],
        'Joint Symptoms': ['Swelling', 'Stiffness', 'Locking', 'Clicking/popping', 'Instability', 'Limited range of motion'],
        'Nerve Symptoms': ['Numbness', 'Tingling', 'Weakness', 'Loss of grip strength', 'Pins and needles'],
        'Other': ['Redness', 'Warmth', 'Bruising', 'Deformity', 'Difficulty with fine movements', 'Trauma/injury']
    },
    legs: {
        'Location': ['Hip', 'Thigh', 'Knee', 'Shin', 'Calf', 'Ankle', 'Foot', 'Toes'],
        'Knee Specific': ['Knee pain', 'Swelling around knee', 'Knee locking', 'Giving way/buckling', 'Knee clicking', 'Knee stiffness', 'Difficulty bending', 'Difficulty straightening'],
        'Pain Type': ['Sharp', 'Dull ache', 'Burning', 'Shooting', 'Throbbing', 'Cramping'],
        'Joint Symptoms': ['Swelling', 'Stiffness', 'Instability', 'Limited range of motion', 'Clicking/popping'],
        'Circulation': ['Coldness', 'Discoloration', 'Varicose veins', 'Swelling (edema)', 'Numbness', 'Tingling'],
        'Mobility': ['Difficulty weight bearing', 'Limping', 'Difficulty walking', 'Difficulty climbing stairs'],
        'Other': ['Redness', 'Warmth', 'Bruising', 'Muscle weakness', 'Cramps at night', 'Restless legs', 'Trauma/injury']
    },
    eyes: {
        'Vision Changes': ['Blurred vision', 'Double vision', 'Floaters', 'Flashes of light', 'Loss of peripheral vision', 'Blind spots', 'Night blindness'],
        'Pain & Discomfort': ['Eye pain', 'Burning', 'Itching', 'Grittiness', 'Foreign body sensation', 'Pressure behind eye'],
        'Appearance': ['Redness', 'Swelling', 'Discharge', 'Watery eyes', 'Dry eyes', 'Yellow discoloration', 'Bulging'],
        'Other': ['Light sensitivity', 'Headache with eye strain', 'Eye twitching', 'Drooping eyelid', 'Crossed eyes', 'Trauma/injury']
    },
    ears: {
        'Ear Symptoms': ['Ear pain', 'Ringing (tinnitus)', 'Hearing loss', 'Muffled hearing', 'Ear fullness', 'Discharge', 'Itching', 'Vertigo'],
        'Throat Symptoms': ['Sore throat', 'Difficulty swallowing', 'Painful swallowing', 'Hoarseness', 'Loss of voice', 'Lump in throat feeling'],
        'Nose & Sinus': ['Runny nose', 'Blocked nose', 'Sneezing', 'Sinus pressure', 'Loss of smell', 'Nosebleeds'],
        'Mouth': ['Mouth ulcers', 'Dry mouth', 'Altered taste', 'Tongue swelling', 'White patches'],
        'Other': ['Swollen glands', 'Neck stiffness', 'Fever', 'Bad breath']
    },
    respiratory: {
        'Breathing': ['Shortness of breath', 'Rapid breathing', 'Wheezing', 'Difficulty breathing at rest', 'Difficulty breathing on exertion', 'Breathing through mouth'],
        'Cough': ['Dry cough', 'Productive cough', 'Coughing blood', 'Night cough', 'Chronic cough', 'Barking cough'],
        'Chest': ['Chest tightness', 'Chest pain when breathing', 'Pleuritic pain (sharp, worse with breathing)'],
        'Other': ['Snoring', 'Sleep apnea', 'Blue lips/fingers', 'Cyanosis', 'Choking sensation', 'Allergies', 'Fever']
    },
    skin: {
        'Rash Type': ['Red rash', 'Raised bumps', 'Flat spots', 'Blisters', 'Hives', 'Scaly patches', 'Eczema-like', 'Psoriasis-like'],
        'Appearance': ['Redness', 'Swelling', 'Discoloration', 'Bruising', 'Yellowing', 'Pale skin', 'Mottled skin'],
        'Texture Changes': ['Dry skin', 'Oily skin', 'Rough patches', 'Thickened skin', 'Thin skin', 'Peeling'],
        'Sensations': ['Itching', 'Burning', 'Pain', 'Numbness', 'Tingling', 'Tenderness'],
        'Growths': ['Moles (changed)', 'Lumps', 'Skin tags', 'Warts', 'Cysts', 'Lesions'],
        'Wounds': ['Open wound', 'Slow-healing wound', 'Ulcer', 'Infection signs', 'Pus', 'Bleeding'],
        'Other': ['Excessive sweating', 'Hair changes', 'Nail changes', 'Sun sensitivity', 'Easy bruising']
    }
};

const durations = [
    { id: 'hours', label: 'A few hours' },
    { id: '1_day', label: '1 day' },
    { id: '2_3_days', label: '2-3 days' },
    { id: '1_week', label: 'About 1 week' },
    { id: '2_weeks', label: '1-2 weeks' },
    { id: '1_month', label: 'About 1 month' },
    { id: 'over_month', label: 'More than a month' },
    { id: 'chronic', label: 'Chronic/ongoing' }
];

const severities = [
    { id: 'mild', label: 'Mild', desc: 'Noticeable but doesn\'t interfere with daily activities' },
    { id: 'moderate', label: 'Moderate', desc: 'Interferes with some daily activities' },
    { id: 'severe', label: 'Severe', desc: 'Significantly affects daily life' },
    { id: 'very_severe', label: 'Very Severe', desc: 'Unable to perform normal activities' }
];

export default function SymptomCheck() {
    const navigate = useNavigate();
    const { language, currentLanguage } = useLanguage();
    const fileInputRef = useRef(null);
    const nextButtonRef = useRef(null);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Disclaimer modal state
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [diagnosisReady, setDiagnosisReady] = useState(false);
    const [diagnosisId, setDiagnosisId] = useState(null);
    
    // Paywall modal state (shown when trial exhausted)
    const [showPaywall, setShowPaywall] = useState(false);
    const [paywallReason, setPaywallReason] = useState('');
    
    // Step 1: Body parts
    const [selectedBodyParts, setSelectedBodyParts] = useState([]);
    
    // Step 2: Detailed Symptoms with expandable sections
    const [selectedSymptoms, setSelectedSymptoms] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Step 3: Duration & Severity
    const [duration, setDuration] = useState('');
    const [severity, setSeverity] = useState('');
    
    // Step 4: Medical History
    const [medicalConditions, setMedicalConditions] = useState([]);
    const [medications, setMedications] = useState([]);
    const [allergies, setAllergies] = useState([]);
    const [surgeries, setSurgeries] = useState([]);
    const [conditionsData, setConditionsData] = useState({});
    const [medicationsData, setMedicationsData] = useState({});
    const [allergiesData, setAllergiesData] = useState({});
    const [surgeriesData, setSurgeriesData] = useState([]);
    
    // Medical history expanded states and custom inputs
    const [expandedMedicalSections, setExpandedMedicalSections] = useState({});
    const [customCondition, setCustomCondition] = useState('');
    const [customMedication, setCustomMedication] = useState('');
    const [customAllergy, setCustomAllergy] = useState('');
    const [customSurgery, setCustomSurgery] = useState('');
    
    // Step 5: Image & Additional Info
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [isVideo, setIsVideo] = useState(false);
    
    // Patient Info (for checking symptoms for others, e.g., child)
    const [checkingFor, setCheckingFor] = useState('self');
    const [patientAge, setPatientAge] = useState('');
    const [patientSex, setPatientSex] = useState('');

    const totalSteps = 5;
    const progress = (step / totalSteps) * 100;

    // Helper function to scroll to next button
    const scrollToNextButton = () => {
        setTimeout(() => {
            if (nextButtonRef.current) {
                nextButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    };

    // Fetch medical history options
    useEffect(() => {
        const fetchMedicalData = async () => {
            try {
                const [condRes, medRes, allRes, surRes] = await Promise.all([
                    axios.get(`${API}/medical-history/conditions`),
                    axios.get(`${API}/medical-history/medications`),
                    axios.get(`${API}/medical-history/allergies`),
                    axios.get(`${API}/medical-history/surgeries`)
                ]);
                setConditionsData(condRes.data);
                setMedicationsData(medRes.data);
                setAllergiesData(allRes.data);
                setSurgeriesData(surRes.data);
            } catch (err) {
                console.error('Failed to fetch medical history options:', err);
            }
        };
        fetchMedicalData();
    }, []);

    const toggleBodyPart = (partId) => {
        const isAdding = !selectedBodyParts.includes(partId);
        setSelectedBodyParts(prev => 
            prev.includes(partId) 
                ? prev.filter(p => p !== partId)
                : [...prev, partId]
        );
        // Auto-expand categories for newly selected body parts
        if (isAdding) {
            setExpandedCategories(prev => ({ ...prev, [partId]: true }));
            // Scroll to Next button after first selection
            if (selectedBodyParts.length === 0) {
                scrollToNextButton();
            }
        }
    };

    const toggleSymptom = (symptom) => {
        const isAdding = !selectedSymptoms.includes(symptom);
        setSelectedSymptoms(prev => 
            prev.includes(symptom) 
                ? prev.filter(s => s !== symptom)
                : [...prev, symptom]
        );
        
        // After selecting a symptom, scroll to the next collapsed category after delay
        if (isAdding) {
            setTimeout(() => {
                // Find the next collapsed task row and scroll to it
                const taskRows = document.querySelectorAll('[data-task-row]');
                let foundCurrent = false;
                
                for (const row of taskRows) {
                    // Check if this row is collapsed (has data-collapsed="true")
                    if (row.getAttribute('data-collapsed') === 'true') {
                        if (foundCurrent || !row.querySelector('.bg-primary')) {
                            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            return;
                        }
                    }
                    // Mark that we've passed the current expanded section
                    if (row.getAttribute('data-collapsed') === 'false') {
                        foundCurrent = true;
                    }
                }
                
                // If no more collapsed rows, scroll to next button
                scrollToNextButton();
            }, 3000); // 3 second delay
        }
    };

    const toggleCategory = (bodyPart, category) => {
        const key = `${bodyPart}-${category}`;
        setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleItem = (item, list, setList) => {
        const isAdding = !list.includes(item);
        setList(prev => 
            prev.includes(item) 
                ? prev.filter(i => i !== item)
                : [...prev, item]
        );
        // Auto-scroll when first item is selected in medical history
        if (isAdding && list.length === 0) {
            scrollToNextButton();
        }
    };

    // Duration change with auto-scroll
    const handleDurationChange = (value) => {
        setDuration(value);
        // Auto-scroll if severity is not yet selected
        if (!severity) {
            setTimeout(() => {
                const severitySection = document.querySelector('[data-testid="severity-mild"]');
                if (severitySection) {
                    severitySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 150);
        } else {
            scrollToNextButton();
        }
    };

    // Severity change with auto-scroll
    const handleSeverityChange = (value) => {
        setSeverity(value);
        // Auto-scroll to next button after selecting severity
        if (duration) {
            scrollToNextButton();
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            setError('File size must be less than 10MB');
            return;
        }

        // Check file type
        const isVideoFile = file.type.startsWith('video/');
        const isImageFile = file.type.startsWith('image/');
        
        if (!isVideoFile && !isImageFile) {
            toast.error('Please upload an image or video file');
            setError('Please upload an image or video file');
            return;
        }

        setIsVideo(isVideoFile);
        setUploadedFileName(file.name);
        setError('');

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            setUploadedImage(base64);
            setImagePreview(reader.result);
            toast.success(`${isVideoFile ? 'Video' : 'Image'} uploaded successfully`);
        };
        reader.onerror = () => {
            toast.error('Failed to read file');
            setError('Failed to read file');
        };
        reader.readAsDataURL(file);
    };

    const removeFile = () => {
        setUploadedImage(null);
        setImagePreview(null);
        setUploadedFileName('');
        setIsVideo(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toast.info('File removed');
    };

    const triggerFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return selectedBodyParts.length > 0;
            case 2: return selectedSymptoms.length > 0;
            case 3: return duration && severity;
            case 4: return true;
            case 5: return true;
            default: return false;
        }
    };

    const handleSubmit = async () => {
        // Show disclaimer modal first
        setShowDisclaimer(true);
        setLoading(true);
        setError('');
        setDiagnosisReady(false);
        setDiagnosisId(null);

        try {
            // Get language settings - ensure we have valid values
            const selectedLanguage = language || 'en';
            const selectedLanguageName = currentLanguage?.name || 
                LANGUAGES.find(l => l.code === selectedLanguage)?.name || 'English';
            
            const payload = {
                body_parts: selectedBodyParts,
                symptoms: selectedSymptoms,
                duration,
                severity,
                additional_info: additionalInfo || null,
                medical_history: {
                    conditions: medicalConditions,
                    medications: medications,
                    allergies: allergies,
                    surgeries: surgeries
                },
                image_data: uploadedImage || null,
                patient_age: patientAge || null,
                patient_sex: patientSex || null,
                checking_for: checkingFor || 'self',
                language: selectedLanguage,
                language_name: selectedLanguageName
            };
            
            console.log('Submitting with language:', selectedLanguage, selectedLanguageName);

            const response = await axios.post(`${API}/symptoms/check`, payload);
            setDiagnosisId(response.data.id);
            setDiagnosisReady(true);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            setShowDisclaimer(false);
            
            // Handle subscription/payment required error (HTTP 402)
            if (err.response?.status === 402) {
                const reason = err.response?.data?.detail?.reason || 'Free trial exhausted';
                setPaywallReason(reason);
                setShowPaywall(true);
                return;
            }
            
            setError(err.response?.data?.detail || 'Failed to generate diagnosis. Please try again.');
        }
    };

    const handleViewDiagnosis = () => {
        if (disclaimerAccepted && diagnosisId) {
            navigate(`/diagnosis/${diagnosisId}`);
        }
    };

    // Auto-scroll to consent checkbox when diagnosis is ready
    useEffect(() => {
        if (diagnosisReady && showDisclaimer && !disclaimerAccepted) {
            // Wait a moment for the UI to update, then scroll to the checkbox
            const timer = setTimeout(() => {
                const checkboxSection = document.getElementById('disclaimer-checkbox-section');
                const disclaimerContent = document.getElementById('disclaimer-content');
                if (checkboxSection && disclaimerContent) {
                    // Smooth scroll within the modal
                    checkboxSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [diagnosisReady, showDisclaimer, disclaimerAccepted]);

    // Helper to scroll to the first task on the page
    const scrollToFirstTask = () => {
        setTimeout(() => {
            const firstTask = document.querySelector('[data-task-row]');
            if (firstTask) {
                firstTask.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    };

    // Helper to scroll to a specific task row
    const scrollToTask = (index) => {
        setTimeout(() => {
            const taskRows = document.querySelectorAll('[data-task-row]');
            if (taskRows[index]) {
                taskRows[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    };

    const nextStep = () => {
        if (step < totalSteps) {
            setStep(step + 1);
            // Scroll to first task on the new step
            scrollToFirstTask();
        } else {
            handleSubmit();
        }
    };

    const prevStep = () => {
        if (step > 1) {
            setStep(step - 1);
            // Scroll to first task on the previous step
            scrollToFirstTask();
        }
    };

    // Toggle medical history section expansion
    const toggleMedicalSection = (sectionId) => {
        const wasExpanded = expandedMedicalSections[sectionId] === true;
        setExpandedMedicalSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
        
        // If we're collapsing (was expanded), scroll to next collapsed section after delay
        if (wasExpanded) {
            setTimeout(() => {
                const taskRows = document.querySelectorAll('[data-task-row]');
                for (const row of taskRows) {
                    if (row.getAttribute('data-collapsed') === 'true') {
                        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        return;
                    }
                }
                // If no more collapsed rows, scroll to next button
                scrollToNextButton();
            }, 300);
        }
    };

    // Add custom item to a list
    const addCustomItem = (customValue, setCustomValue, selected, setSelected) => {
        const trimmed = customValue.trim();
        if (trimmed && !selected.includes(trimmed)) {
            setSelected(prev => [...prev, trimmed]);
            setCustomValue('');
            toast.success(`Added "${trimmed}"`);
        }
    };

    // Render collapsible medical history section with free text input
    const renderMedicalHistorySection = (sectionId, title, icon, data, selected, setSelected, customValue, setCustomValue, isNextTask = false, isFlat = false) => {
        const Icon = icon;
        const items = isFlat ? data : Object.values(data).flat();
        const isExpanded = expandedMedicalSections[sectionId] === true;
        const selectedCount = selected.length;
        
        return (
            <div 
                className={`rounded-xl overflow-hidden bg-card transition-all duration-500
                    ${isNextTask 
                        ? 'border-2 border-primary shadow-[0_0_15px_rgba(20,184,166,0.4)] animate-pulse' 
                        : 'border border-slate-200 dark:border-slate-700'
                    }`}
                data-task-row="true"
                data-collapsed={!isExpanded}
            >
                {/* Collapsible Header */}
                <button
                    type="button"
                    onClick={() => toggleMedicalSection(sectionId)}
                    className={`w-full flex items-center justify-between p-4 transition-colors group
                        ${isNextTask 
                            ? 'bg-primary/10 hover:bg-primary/20' 
                            : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                >
                    <span className="font-medium flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-primary" />
                        </div>
                        {title}
                        {selectedCount > 0 && (
                            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                                {selectedCount} selected
                            </span>
                        )}
                    </span>
                    <div className="flex items-center gap-1">
                        {isNextTask && (
                            <span className="text-xs text-primary font-semibold">Click here</span>
                        )}
                        <ChevronDown 
                            className={`w-5 h-5 text-primary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                        />
                    </div>
                </button>
                
                {/* Expandable Content */}
                <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="p-4 space-y-4">
                        {/* Predefined Options */}
                        <div className="max-h-40 overflow-y-auto">
                            <div className="flex flex-wrap gap-2">
                                {items.map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        onClick={() => toggleItem(item, selected, setSelected)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border
                                            ${selected.includes(item)
                                                ? 'bg-primary text-white border-primary'
                                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary/50'
                                            }`}
                                        data-testid={`medical-${item.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Custom Input */}
                        <div className="pt-2 border-t">
                            <Label className="text-xs text-muted-foreground mb-2 block">
                                Can&apos;t find what you&apos;re looking for? Add your own:
                            </Label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    placeholder={`Enter custom ${title.toLowerCase().replace('pre-existing ', '').replace('known ', '').replace('previous ', '').replace('current ', '')}...`}
                                    className="flex-1 px-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addCustomItem(customValue, setCustomValue, selected, setSelected);
                                        }
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addCustomItem(customValue, setCustomValue, selected, setSelected)}
                                    disabled={!customValue.trim()}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>
                        
                        {/* Selected Items Display */}
                        {selectedCount > 0 && (
                            <div className="pt-2 border-t">
                                <p className="text-xs text-muted-foreground mb-2">Selected items:</p>
                                <div className="flex flex-wrap gap-1">
                                    {selected.map(item => (
                                        <span 
                                            key={item} 
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                                        >
                                            {item}
                                            <button
                                                type="button"
                                                onClick={() => setSelected(prev => prev.filter(i => i !== item))}
                                                className="hover:bg-primary/20 rounded-full p-0.5"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render detailed symptoms for a body part with expandable categories
    const renderDetailedSymptoms = (bodyPartId, bodyPartIndex) => {
        const symptoms = detailedSymptomsByBodyPart[bodyPartId];
        if (!symptoms) return null;

        const bodyPartLabel = bodyParts.find(bp => bp.id === bodyPartId)?.label || bodyPartId;
        const BodyPartIcon = bodyParts.find(bp => bp.id === bodyPartId)?.icon || Activity;
        const categories = Object.entries(symptoms);

        // Find the first collapsed category index for this body part
        const getFirstCollapsedIndex = () => {
            for (let i = 0; i < categories.length; i++) {
                const categoryKey = `${bodyPartId}-${categories[i][0]}`;
                if (expandedCategories[categoryKey] !== true) {
                    return i;
                }
            }
            return -1;
        };
        const firstCollapsedIdx = getFirstCollapsedIndex();

        return (
            <div key={bodyPartId} className="border rounded-xl p-4 bg-card mb-4" data-bodypart={bodyPartId}>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BodyPartIcon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg">{bodyPartLabel}</h3>
                </div>
                
                <div className="space-y-2">
                    {categories.map(([category, symptomList], categoryIndex) => {
                        const categoryKey = `${bodyPartId}-${category}`;
                        const isExpanded = expandedCategories[categoryKey] === true;
                        const selectedCount = symptomList.filter(s => selectedSymptoms.includes(s)).length;
                        const isNextTask = !isExpanded && categoryIndex === firstCollapsedIdx;

                        return (
                            <div 
                                key={category} 
                                className={`rounded-lg overflow-hidden transition-all duration-500
                                    ${isNextTask 
                                        ? 'border-2 border-primary shadow-[0_0_15px_rgba(20,184,166,0.4)] animate-pulse' 
                                        : 'border border-slate-200 dark:border-slate-700'
                                    }`}
                                data-task-row="true"
                                data-collapsed={!isExpanded}
                            >
                                <button
                                    type="button"
                                    onClick={() => toggleCategory(bodyPartId, category)}
                                    className={`w-full flex items-center justify-between p-3 transition-colors group
                                        ${isNextTask 
                                            ? 'bg-primary/10 hover:bg-primary/20' 
                                            : 'bg-muted/30 hover:bg-muted/50'
                                        }`}
                                >
                                    <span className="font-medium text-sm flex items-center gap-2">
                                        {category}
                                        {selectedCount > 0 && (
                                            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                                                {selectedCount} selected
                                            </span>
                                        )}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        {isNextTask && (
                                            <span className="text-xs text-primary font-semibold">Click here</span>
                                        )}
                                        <ChevronDown 
                                            className={`w-5 h-5 text-primary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                                        />
                                    </div>
                                </button>
                                
                                <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className="p-3 flex flex-wrap gap-2">
                                        {symptomList.map((symptom) => (
                                            <button
                                                key={symptom}
                                                type="button"
                                                onClick={() => toggleSymptom(symptom)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border
                                                    ${selectedSymptoms.includes(symptom)
                                                        ? 'bg-primary text-white border-primary'
                                                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary/50'
                                                    }`}
                                            >
                                                {symptom}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="symptom-check-page">
            <div className="max-w-3xl mx-auto px-4">
                {/* Header */}
                <div className="mb-8">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/dashboard')}
                        className="mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Dashboard
                    </Button>
                    <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">
                        Symptom Assessment
                    </h1>
                    <p className="text-muted-foreground">
                        Tell us about your symptoms to receive an AI-powered health assessment
                    </p>
                </div>

                {/* Progress */}
                <div className="mb-8">
                    <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Step {step} of {totalSteps}</span>
                        <span className="font-medium">{Math.round(progress)}% complete</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                        <span className={step >= 1 ? 'text-primary font-medium' : ''}>Body</span>
                        <span className={step >= 2 ? 'text-primary font-medium' : ''}>Symptoms</span>
                        <span className={step >= 3 ? 'text-primary font-medium' : ''}>Details</span>
                        <span className={step >= 4 ? 'text-primary font-medium' : ''}>History</span>
                        <span className={step >= 5 ? 'text-primary font-medium' : ''}>Upload</span>
                    </div>
                </div>

                {/* Form Card */}
                <Card className="shadow-lg border-slate-200 dark:border-slate-700">
                    {/* Step 1: Body Parts */}
                    {step === 1 && (
                        <>
                            <CardHeader>
                                <CardTitle className="font-heading">Select Affected Body Parts</CardTitle>
                                <CardDescription>
                                    Choose all body parts where symptoms are present
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Who is this check for */}
                                <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                                    <Label className="text-base font-medium mb-3 block">Who is this symptom check for?</Label>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                                        {[
                                            { id: 'self', label: 'Myself' },
                                            { id: 'child', label: 'My Child' },
                                            { id: 'family_member', label: 'Family Member' },
                                            { id: 'other', label: 'Someone Else' }
                                        ].map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => setCheckingFor(option.id)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                                    checkingFor === option.id
                                                        ? 'bg-primary text-white'
                                                        : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    {checkingFor !== 'self' && (
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                            <div className="space-y-2">
                                                <Label className="text-sm">Patient Age</Label>
                                                <input
                                                    type="text"
                                                    value={patientAge}
                                                    onChange={(e) => setPatientAge(e.target.value)}
                                                    placeholder="e.g., 5 years, 3 months"
                                                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm">Patient Sex</Label>
                                                <select
                                                    value={patientSex}
                                                    onChange={(e) => setPatientSex(e.target.value)}
                                                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-slate-900"
                                                >
                                                    <option value="">Select</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Body Parts Selection */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                    {bodyParts.map((part) => {
                                        const Icon = part.icon;
                                        const isSelected = selectedBodyParts.includes(part.id);
                                        return (
                                            <button
                                                key={part.id}
                                                type="button"
                                                onClick={() => toggleBodyPart(part.id)}
                                                className={`body-part-card flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all
                                                    ${isSelected 
                                                        ? 'selected' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'}`}
                                                data-testid={`body-part-${part.id}`}
                                            >
                                                <Icon className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                                <span className={`text-sm font-medium ${isSelected ? 'text-primary' : ''}`}>
                                                    {part.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedBodyParts.length > 0 && (
                                    <div className="mt-4 p-3 bg-primary/5 rounded-lg">
                                        <p className="text-sm font-medium text-primary">
                                            Selected: {selectedBodyParts.map(p => bodyParts.find(bp => bp.id === p)?.label).join(', ')}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </>
                    )}

                    {/* Step 2: Detailed Symptoms with Dropdowns */}
                    {step === 2 && (
                        <>
                            <CardHeader>
                                <CardTitle className="font-heading">Describe Your Symptoms</CardTitle>
                                <CardDescription>
                                    Select all symptoms you&apos;re currently experiencing for each body part
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Quick Photo Upload for Visual Symptoms */}
                                {(selectedBodyParts.includes('skin') || selectedBodyParts.includes('eyes') || selectedBodyParts.includes('legs') || selectedBodyParts.includes('arms')) && (
                                    <div className="p-4 border-2 border-dashed border-amber-300 dark:border-amber-600 rounded-xl bg-amber-50 dark:bg-amber-950/20 mb-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                                                <Camera className="w-5 h-5 text-amber-600" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-amber-800 dark:text-amber-200">
                                                    Have a visible symptom?
                                                </h4>
                                                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                                    For rashes, swelling, skin discolorations, or injuries - uploading a photo greatly improves diagnosis accuracy.
                                                </p>
                                                <div className="mt-3">
                                                    {!imagePreview ? (
                                                        <Button 
                                                            type="button" 
                                                            variant="outline" 
                                                            size="sm"
                                                            className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900"
                                                            onClick={triggerFileUpload}
                                                        >
                                                            <Camera className="w-4 h-4 mr-2" />
                                                            Upload Photo Now
                                                        </Button>
                                                    ) : (
                                                        <div className="flex items-center gap-2 text-sm text-green-600">
                                                            <CheckCircle2 className="w-4 h-4" />
                                                            {isVideo ? 'Video' : 'Photo'} uploaded
                                                            <Button 
                                                                type="button" 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={removeFile}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Render detailed symptoms for each selected body part */}
                                {selectedBodyParts.map((bodyPartId, index) => renderDetailedSymptoms(bodyPartId, index))}

                                {selectedSymptoms.length > 0 && (
                                    <div className="p-3 bg-primary/5 rounded-lg">
                                        <p className="text-sm font-medium text-primary mb-2">
                                            {selectedSymptoms.length} symptom{selectedSymptoms.length > 1 ? 's' : ''} selected:
                                        </p>
                                        <div className="flex flex-wrap gap-1">
                                            {selectedSymptoms.map(s => (
                                                <span key={s} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </>
                    )}

                    {/* Step 3: Duration & Severity */}
                    {step === 3 && (
                        <>
                            <CardHeader>
                                <CardTitle className="font-heading">Duration & Severity</CardTitle>
                                <CardDescription>
                                    Help us understand how long and how severe your symptoms are
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Duration */}
                                <div 
                                    className={`space-y-3 p-4 rounded-xl transition-all duration-500
                                        ${!duration 
                                            ? 'border-2 border-primary shadow-[0_0_15px_rgba(20,184,166,0.4)] bg-primary/5' 
                                            : 'border border-transparent'
                                        }`}
                                    data-task-row="true"
                                    data-collapsed={!duration}
                                >
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-medium">How long have you had these symptoms?</Label>
                                        {!duration && <span className="text-xs text-primary font-semibold">Select one</span>}
                                    </div>
                                    <Select value={duration} onValueChange={handleDurationChange}>
                                        <SelectTrigger data-testid="duration-select">
                                            <SelectValue placeholder="Select duration" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {durations.map((d) => (
                                                <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Severity */}
                                <div 
                                    className={`space-y-3 p-4 rounded-xl transition-all duration-500
                                        ${duration && !severity 
                                            ? 'border-2 border-primary shadow-[0_0_15px_rgba(20,184,166,0.4)] bg-primary/5' 
                                            : 'border border-transparent'
                                        }`}
                                    data-task-row="true"
                                    data-collapsed={!severity}
                                >
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-medium">How severe are your symptoms?</Label>
                                        {duration && !severity && <span className="text-xs text-primary font-semibold">Select one</span>}
                                    </div>
                                    <div className="grid gap-3">
                                        {severities.map((s) => (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onClick={() => handleSeverityChange(s.id)}
                                                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all
                                                    ${severity === s.id 
                                                        ? 'border-primary bg-primary/5' 
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'}`}
                                                data-testid={`severity-${s.id}`}
                                            >
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5
                                                    ${severity === s.id ? 'border-primary bg-primary' : 'border-slate-300'}`}>
                                                    {severity === s.id && <CheckCircle2 className="w-3 h-3 text-white" />}
                                                </div>
                                                <div>
                                                    <p className="font-medium">{s.label}</p>
                                                    <p className="text-sm text-muted-foreground">{s.desc}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Step 4: Medical History */}
                    {step === 4 && (
                        <>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Stethoscope className="w-5 h-5 text-primary" />
                                    Medical History
                                </CardTitle>
                                <CardDescription>
                                    Select any relevant medical conditions, medications, or allergies (optional but helps improve diagnosis). Click each section to expand.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {(() => {
                                    // Determine which section is the "next" task (first unexpanded)
                                    const sections = ['conditions', 'medications', 'allergies', 'surgeries'];
                                    const firstCollapsed = sections.find(s => expandedMedicalSections[s] !== true);
                                    
                                    return (
                                        <>
                                            {renderMedicalHistorySection(
                                                'conditions',
                                                'Pre-existing Conditions',
                                                Heart,
                                                conditionsData,
                                                medicalConditions,
                                                setMedicalConditions,
                                                customCondition,
                                                setCustomCondition,
                                                firstCollapsed === 'conditions'
                                            )}
                                            
                                            {renderMedicalHistorySection(
                                                'medications',
                                                'Current Medications',
                                                Pill,
                                                medicationsData,
                                                medications,
                                                setMedications,
                                                customMedication,
                                                setCustomMedication,
                                                firstCollapsed === 'medications'
                                            )}
                                            
                                            {renderMedicalHistorySection(
                                                'allergies',
                                                'Known Allergies',
                                                AlertTriangle,
                                                allergiesData,
                                                allergies,
                                                setAllergies,
                                                customAllergy,
                                                setCustomAllergy,
                                                firstCollapsed === 'allergies'
                                            )}
                                            
                                            {renderMedicalHistorySection(
                                                'surgeries',
                                                'Previous Surgeries',
                                                Activity,
                                                surgeriesData,
                                                surgeries,
                                                setSurgeries,
                                                customSurgery,
                                                setCustomSurgery,
                                                firstCollapsed === 'surgeries',
                                                true
                                            )}
                                        </>
                                    );
                                })()}
                            </CardContent>
                        </>
                    )}

                    {/* Step 5: Image Upload & Additional Info */}
                    {step === 5 && (
                        <>
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <Camera className="w-5 h-5 text-primary" />
                                    Visual Evidence & Notes
                                </CardTitle>
                                <CardDescription>
                                    Upload photos or videos of visible symptoms for more accurate diagnosis (optional)
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Hidden file input */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    data-testid="file-input"
                                />

                                {/* File Upload Section */}
                                <div 
                                    className={`space-y-3 p-4 rounded-xl transition-all duration-500
                                        ${!uploadedImage 
                                            ? 'border-2 border-primary shadow-[0_0_15px_rgba(20,184,166,0.4)] bg-primary/5' 
                                            : 'border border-transparent'
                                        }`}
                                    data-task-row="true"
                                    data-collapsed={!uploadedImage}
                                >
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-medium">
                                            Upload Photo or Video of Symptoms
                                        </Label>
                                        {!uploadedImage && <span className="text-xs text-primary font-semibold">Optional</span>}
                                    </div>
                                    
                                    {/* Visual symptom examples */}
                                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
                                            📸 Helpful for diagnosing:
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {['Rashes', 'Swelling', 'Skin discoloration', 'Lesions', 'Bruising', 'Wounds', 'Eye redness', 'Joint swelling'].map(item => (
                                                <span key={item} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {!imagePreview ? (
                                        <div 
                                            className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                                            onClick={triggerFileUpload}
                                            data-testid="upload-area"
                                        >
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <Upload className="w-6 h-6 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">Click to upload photo or video</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        PNG, JPG, GIF, MP4, MOV up to 10MB
                                                    </p>
                                                </div>
                                                <Button type="button" variant="outline" className="mt-2">
                                                    <Camera className="w-4 h-4 mr-2" />
                                                    Choose File
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative border rounded-xl p-4 bg-muted/30">
                                            {isVideo ? (
                                                <div className="flex items-center gap-4">
                                                    <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                                                        <Video className="w-8 h-8 text-primary" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="font-medium">{uploadedFileName}</p>
                                                        <p className="text-sm text-muted-foreground">Video uploaded successfully</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <img
                                                    src={imagePreview}
                                                    alt="Uploaded preview"
                                                    className="w-full max-h-64 object-contain rounded-lg"
                                                    data-testid="image-preview"
                                                />
                                            )}
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                size="icon"
                                                className="absolute top-2 right-2 rounded-full"
                                                onClick={removeFile}
                                                data-testid="remove-file-btn"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                            <div className="flex items-center gap-2 mt-3 text-sm text-green-600">
                                                {isVideo ? <Video className="w-4 h-4" /> : <FileImage className="w-4 h-4" />}
                                                {isVideo ? 'Video' : 'Image'} uploaded successfully
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Additional Info */}
                                <div 
                                    className="space-y-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700"
                                    data-task-row="true"
                                >
                                    <Label className="text-base font-medium">
                                        Additional Information (Optional)
                                    </Label>
                                    <Textarea
                                        placeholder="e.g., Recent injuries, when symptoms started, what makes it better or worse, any triggers you've noticed..."
                                        value={additionalInfo}
                                        onChange={(e) => setAdditionalInfo(e.target.value)}
                                        rows={4}
                                        className="resize-none"
                                        data-testid="additional-info-input"
                                    />
                                </div>

                                {/* Summary */}
                                <div className="p-4 bg-muted/50 rounded-xl space-y-3">
                                    <h4 className="font-medium flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        Assessment Summary
                                    </h4>
                                    <div className="text-sm space-y-2 text-muted-foreground">
                                        <p><strong>Body parts:</strong> {selectedBodyParts.map(p => bodyParts.find(bp => bp.id === p)?.label).join(', ')}</p>
                                        <p><strong>Symptoms:</strong> {selectedSymptoms.length} selected</p>
                                        <p><strong>Duration:</strong> {durations.find(d => d.id === duration)?.label}</p>
                                        <p><strong>Severity:</strong> {severities.find(s => s.id === severity)?.label}</p>
                                        {medicalConditions.length > 0 && (
                                            <p><strong>Conditions:</strong> {medicalConditions.length} selected</p>
                                        )}
                                        {uploadedImage && (
                                            <p><strong>{isVideo ? 'Video' : 'Image'}:</strong> ✓ Uploaded</p>
                                        )}
                                    </div>
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}
                            </CardContent>
                        </>
                    )}

                    {/* Navigation */}
                    <div className="p-6 pt-0 flex justify-between">
                        <Button
                            variant="ghost"
                            onClick={prevStep}
                            disabled={step === 1 || loading}
                            data-testid="prev-step-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Previous
                        </Button>
                        <Button
                            ref={nextButtonRef}
                            onClick={nextStep}
                            disabled={!canProceed() || loading}
                            className="rounded-full px-6"
                            data-testid="next-step-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Analyzing...
                                </>
                            ) : step === totalSteps ? (
                                <>
                                    Get Assessment
                                    <Stethoscope className="w-4 h-4 ml-2" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                </Card>
            </div>

            {/* Disclaimer Modal */}
            {showDisclaimer && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
                        <div className="p-6 space-y-6 overflow-y-auto flex-1" id="disclaimer-content">
                            {/* Header */}
                            <div className="text-center">
                                <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mx-auto flex items-center justify-center mb-4">
                                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                                </div>
                                <h2 className="font-heading text-xl font-bold">Important Medical Disclaimer</h2>
                            </div>

                            {/* Loading/Processing State */}
                            {loading && (
                                <div className="flex flex-col items-center py-4">
                                    <Loader2 className="w-10 h-10 text-primary animate-spin mb-3" />
                                    <p className="text-muted-foreground">Analyzing your symptoms...</p>
                                </div>
                            )}

                            {/* Diagnosis Ready State */}
                            {diagnosisReady && (
                                <div className="flex flex-col items-center py-2">
                                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
                                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                                    </div>
                                    <p className="text-green-600 font-medium">Assessment Ready</p>
                                </div>
                            )}

                            {/* Disclaimer Text */}
                            <div className="bg-muted/50 rounded-xl p-4 text-sm">
                                <p className="font-medium text-foreground mb-3">Please read and acknowledge the following:</p>
                                <div className="space-y-3 text-muted-foreground">
                                    <p className="flex gap-2">
                                        <span className="text-amber-500 flex-shrink-0">•</span>
                                        <span>This assessment is AI-generated and is intended for informational purposes only.</span>
                                    </p>
                                    <p className="flex gap-2">
                                        <span className="text-amber-500 flex-shrink-0">•</span>
                                        <span>It does NOT replace professional medical advice, diagnosis, or treatment.</span>
                                    </p>
                                    <p className="flex gap-2">
                                        <span className="text-amber-500 flex-shrink-0">•</span>
                                        <span>You remain fully responsible for seeking appropriate medical attention based on the severity of your symptoms.</span>
                                    </p>
                                    <p className="flex gap-2">
                                        <span className="text-amber-500 flex-shrink-0">•</span>
                                        <span>If you are experiencing a medical emergency, please call emergency services or visit your nearest Emergency Room (ER) immediately.</span>
                                    </p>
                                    <p className="flex gap-2">
                                        <span className="text-amber-500 flex-shrink-0">•</span>
                                        <span>Always consult with a qualified healthcare provider for any health concerns.</span>
                                    </p>
                                </div>
                            </div>

                            {/* Scroll indicator - shows when diagnosis is ready */}
                            {diagnosisReady && !disclaimerAccepted && (
                                <div className="flex flex-col items-center py-2 animate-bounce">
                                    <ChevronDown className="w-6 h-6 text-primary" />
                                    <p className="text-sm text-primary font-medium">Scroll down to confirm</p>
                                </div>
                            )}

                            {/* Checkbox - with ref for auto-scroll */}
                            <div 
                                id="disclaimer-checkbox-section"
                                className={`flex items-start gap-3 p-4 border-2 rounded-xl transition-all duration-300 ${
                                    diagnosisReady && !disclaimerAccepted 
                                        ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-2 animate-pulse' 
                                        : 'border-primary/30 bg-primary/5'
                                }`}
                            >
                                <Checkbox
                                    id="disclaimer-checkbox"
                                    checked={disclaimerAccepted}
                                    onCheckedChange={setDisclaimerAccepted}
                                    className="mt-0.5"
                                    data-testid="disclaimer-checkbox"
                                />
                                <label 
                                    htmlFor="disclaimer-checkbox" 
                                    className="text-sm cursor-pointer select-none"
                                >
                                    <span className="font-semibold text-foreground">I understand and acknowledge</span> that this is an AI-generated assessment and does not replace professional medical advice. I take full responsibility for seeking appropriate medical care.
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                        setShowDisclaimer(false);
                                        setDisclaimerAccepted(false);
                                        setLoading(false);
                                    }}
                                    data-testid="disclaimer-cancel-btn"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="flex-1"
                                    disabled={!disclaimerAccepted || !diagnosisReady}
                                    onClick={handleViewDiagnosis}
                                    data-testid="disclaimer-view-btn"
                                >
                                    {diagnosisReady ? (
                                        <>
                                            View My Assessment
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    ) : (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Processing...
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Paywall Modal - Shows when trial is exhausted */}
            {showPaywall && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                        {/* Header with gradient */}
                        <div className="bg-gradient-to-r from-primary to-accent p-6 text-white text-center">
                            <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
                                <Crown className="w-8 h-8" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">Upgrade to Continue</h2>
                            <p className="text-white/90 text-sm">{paywallReason}</p>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4">
                            <p className="text-center text-muted-foreground">
                                You've used all your free assessments. Upgrade now to get unlimited access to AI-powered health insights!
                            </p>

                            {/* Plan options */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 p-4 border-2 border-primary/30 rounded-xl bg-primary/5 hover:border-primary transition-colors cursor-pointer" onClick={() => navigate('/subscription')}>
                                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                        <Zap className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold">Pay Per Use</p>
                                        <p className="text-sm text-muted-foreground">$1.99 per assessment</p>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-primary" />
                                </div>

                                <div className="flex items-center gap-3 p-4 border-2 border-accent/30 rounded-xl bg-accent/5 hover:border-accent transition-colors cursor-pointer" onClick={() => navigate('/subscription')}>
                                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-accent" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold">Basic Monthly</p>
                                        <p className="text-sm text-muted-foreground">$19.99/mo - Unlimited checks</p>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-accent" />
                                </div>

                                <div className="flex items-center gap-3 p-4 border-2 border-amber-500/30 rounded-xl bg-amber-50 dark:bg-amber-950/20 hover:border-amber-500 transition-colors cursor-pointer" onClick={() => navigate('/subscription')}>
                                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                        <Crown className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold text-amber-700 dark:text-amber-400">Premium Monthly</p>
                                        <p className="text-sm text-muted-foreground">$49.99/mo - Unlimited + consultations</p>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-amber-600" />
                                </div>
                            </div>

                            {/* Benefits */}
                            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                                <p className="font-medium mb-2 text-sm">What you get:</p>
                                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span>Unlimited assessments</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span>PDF exports</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span>Image analysis</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span>Priority support</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="p-6 pt-0 flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setShowPaywall(false)}
                                data-testid="paywall-cancel-btn"
                            >
                                Maybe Later
                            </Button>
                            <Button
                                className="flex-1 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                                onClick={() => navigate('/subscription')}
                                data-testid="paywall-upgrade-btn"
                            >
                                <CreditCard className="w-4 h-4 mr-2" />
                                View Plans
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
